import type { SnackBarConfig } from '@shared/components/Snackbar';
import { Box, styled as muiStyled } from '@mui/material';
import React, { useState } from 'react';
import CustomSnackbar from '@shared/components/Snackbar';
import { ReviewStatus, ReviewStep } from '@shared/domain/review';
import { MOBILE_BREAKPOINT } from '@shared/styles/Breakpoints';
import BasicReviewForm from '../components/review_forms/BasicReviewForm';
import EntryStrategyReviewForm from '../components/review_forms/EntryStrategyReviewForm';
import EvaluationReviewForm from '../components/review_forms/EvaluationReviewForm';
import LegalReviewForm from '../components/review_forms/LegalReviewForm';
import NegotiationReviewForm from '../components/review_forms/NegotiationReviewForm';
import PurchaseDetailsReviewForm from '../components/review_forms/PurchaseDetailsReviewForm';
import RfpReviewForm from '../components/review_forms/RfpReviewForm';

const SectionCard = muiStyled(Box)({
  background: '#fff',
  borderRadius: '12px',
  boxShadow: '0 6px 18px rgba(16, 24, 40, 0.08)',
  [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
    padding: '12px',
  },
});

interface EditReviewSectionProps {
  sectionKey: ReviewStep;
  review: any;
  updateReviewStatus?: (status: ReviewStatus) => void;
}

function EditReviewSection({
  sectionKey,
  review,
  updateReviewStatus,
}: EditReviewSectionProps) {
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  // Helper to map a review section to flat default values
  const mapSectionToDefaults = (section: any): Record<string, any> => {
    const out: Record<string, any> = {};
    if (!section || typeof section !== 'object') return out;

    Object.entries(section).forEach(([key, value]: [string, any]) => {
      if (key === 'ratings') return;
      if (value && typeof value === 'object' && 'answer' in value) {
        out[key] = (value as any).answer;
      }
    });

    if (Array.isArray(section.ratings)) {
      section.ratings.forEach((r: any) => {
        if (r && r.label) {
          out[r.label] =
            typeof r.answer === 'string' || typeof r.answer === 'number'
              ? r.answer
              : null;
        }
      });
    }

    return out;
  };

  const getSectionByKey = () => {
    switch (sectionKey) {
      case ReviewStep.BASIC_REVIEW:
        return review?.reviewBasic;
      case ReviewStep.PURCHASE_DETAILS:
        return review?.reviewPurchaseDetails;
      case ReviewStep.ENTRY_STRATEGY:
        return review?.reviewEntryStrategy;
      case ReviewStep.EVALUATION:
        return review?.reviewEvaluation;
      case ReviewStep.LEGAL:
        return review?.reviewLegal;
      case ReviewStep.NEGOTIATION:
        return review?.reviewNegotiation;
      case ReviewStep.RFP:
        return review?.reviewRfp;
      default:
        return null;
    }
  };

  const sectionData = getSectionByKey();
  const defaultValues: Record<string, any> = {
    id: review?.id || null,
    ...mapSectionToDefaults(sectionData),
  };

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  const handleEditSuccess = (updatedStatus?: ReviewStatus) => {
    setSnackbarConfig({
      open: true,
      type: 'success',
      message: 'Changes saved successfully.',
    });

    // Update the review status without refetching the entire review
    if (updatedStatus && updateReviewStatus) {
      updateReviewStatus(updatedStatus);
    }
  };

  return (
    <>
      <SectionCard>
        {sectionKey === ReviewStep.BASIC_REVIEW && (
          <BasicReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
          />
        )}
        {sectionKey === ReviewStep.PURCHASE_DETAILS && (
          <PurchaseDetailsReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
          />
        )}
        {sectionKey === ReviewStep.ENTRY_STRATEGY && (
          <EntryStrategyReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
          />
        )}
        {sectionKey === ReviewStep.EVALUATION && (
          <EvaluationReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
          />
        )}
        {sectionKey === ReviewStep.NEGOTIATION && (
          <NegotiationReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
          />
        )}
        {sectionKey === ReviewStep.LEGAL && (
          <LegalReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
          />
        )}
        {sectionKey === ReviewStep.RFP && (
          <RfpReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
          />
        )}
      </SectionCard>
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </>
  );
}

export default EditReviewSection;
